import { useContext, useState, useEffect, useRef } from 'react'
import { Dropdown, Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useMatch } from 'react-router-dom'

import { MAX_MOBILE_WIDTH } from '../../constants/layout.js'
import { ID_PERFIL_VENDEDOR } from '../../constants/perfiles.js'
import { cerrarSesion } from '../../helpers/cerrarSesion'
import { obtenerFechaInicioMesSiguiente } from '../../helpers/dateValidation.js'
import {
  listaEnviosVencidas,
  publicacionesVencidas,
  tiendaOficialVencimiento
} from '../../helpers/notificarVencimientos.js'
import { useAuth } from '../../hooks/useAuth'
import logoEBM from '../../img/logoEBMCabecera.png'
import { setIsMobile } from '../../slices/clientDataSlice'
import { CarritoContext } from '../context/carritoContext'
import { MenuContext } from '../context/menuContext'
import { formatearNumeroAR } from '../helpers/formatearNumeroAR.js'
import { useDeshabilitaCompra } from '../hooks/deshabilitaCompra.js'
import ImagenPerfil from '../imagen/ImagenPerfil'
import MenuFavoritos from '../MenuFavoritos'
import { buscarListaPorEmpresa } from '../servicios/listaEnvioService.js'
import { buscarTodasOptimizado } from '../servicios/publicacionesService'
import { buscarPorEmpresa } from '../servicios/tiendasOficialesService.js'
import LinkButton from '../ui/LinkButton'
import styles from './Cabecera.module.css'
import CabeceraBuscador from './CabeceraBuscador'
import DropdownNotificacion from './DropdownNotificacion'

const UMBRAL_SALDO_CRITICO = 25
const UMBRAL_SALDO_BAJO = 50
const PORCENTAJE_MAXIMO = 100

const Cabecera = () => {
  const { desplegarMenu, menuAbierto } = useContext(MenuContext)
  const { cantidad, setVerCarrito } = useContext(CarritoContext)
  const {
    loginDataContext: { idUsuario, nombre, empid, idPerfil } = {},
    logout,
    mostrarAvisoSesion,
    extenderSesion,
    cerrarSesionPorInactividad
  } = useAuth()
  const { saldoMensualDisponible, limiteMensualTotal, limitePorCompra } = useDeshabilitaCompra()
  const porcentajeDisponible =
    limiteMensualTotal > 0
      ? Math.min(
          PORCENTAJE_MAXIMO,
          Math.max(0, (saldoMensualDisponible / limiteMensualTotal) * PORCENTAJE_MAXIMO)
        )
      : 0
  const [refreshNotificaciones, setRefreshNotificaciones] = useState(false)
  const [verDetalleSaldo, setVerDetalleSaldo] = useState(false)
  const saldoRef = useRef(null)
  const headerRef = useRef(null)
  const dispatch = useDispatch()
  const { isMobile } = useSelector((state) => state.clientData)
  const enPerfil = Boolean(useMatch('/Perfil'))

  const leyendaSaldo =
    saldoMensualDisponible === null
      ? ''
      : limitePorCompra !== null
        ? `Máx. por compra $${formatearNumeroAR(
            limitePorCompra
          )}.\nFecha de renovación: ${obtenerFechaInicioMesSiguiente()}`
        : `\nFecha de renovación: ${obtenerFechaInicioMesSiguiente()}`

  const handleRevokeToken = () => {
    cerrarSesion(logout)
  }

  const fetchData = async (idUsuario, empid) => {
    const { data: publicaciones } = await buscarTodasOptimizado(0, idUsuario, 0, '')
    const { data: tienda } = await buscarPorEmpresa(empid)
    const { data: listaEnvios } = await buscarListaPorEmpresa(empid)

    let notificaTienda = false
    let notificaPublicacion = false
    let notificaListaEnvios = false

    if (tienda) {
      notificaTienda = await tiendaOficialVencimiento(tienda, idUsuario, publicaciones)
    }

    if (publicaciones.length > 0) {
      notificaPublicacion = await publicacionesVencidas(publicaciones, idUsuario)
    }

    if (listaEnvios.length > 0) {
      notificaListaEnvios = await listaEnviosVencidas(listaEnvios, idUsuario)
    }
    if (notificaPublicacion || notificaTienda || notificaListaEnvios) {
      setRefreshNotificaciones(true)
    }
  }

  useEffect(() => {
    if (idUsuario && empid) {
      fetchData(idUsuario, empid)
    }
  }, [idUsuario, empid])

  useEffect(() => {
    // Detectar cambios en el tamaño de la ventana para actualizar el estado de isMobile.
    // Se ejecuta solo cuando pasa de un lado de MAX_MOBILE_WIDTH al otro.
    const mediaQuery = window.matchMedia(`(max-width: ${MAX_MOBILE_WIDTH}px)`)
    const onChange = (e) => dispatch(setIsMobile(e.matches))

    mediaQuery.addEventListener('change', onChange)
    dispatch(setIsMobile(mediaQuery.matches))

    return () => mediaQuery.removeEventListener('change', onChange)
  }, [dispatch])

  useEffect(() => {
    // Publica el borde inferior real de la cabecera en --altura-cabecera para que el menú
    // principal (position: fixed) se abra pegado a ella en cualquier tamaño de pantalla.
    const cabecera = headerRef.current?.closest('header') ?? headerRef.current
    if (!cabecera) return

    const actualizarAltura = () => {
      const { bottom } = cabecera.getBoundingClientRect()
      document.documentElement.style.setProperty('--altura-cabecera', `${Math.round(bottom)}px`)
    }

    actualizarAltura()
    const observer = new ResizeObserver(actualizarAltura)
    observer.observe(cabecera)
    window.addEventListener('resize', actualizarAltura)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', actualizarAltura)
    }
  }, [])

  useEffect(() => {
    if (!verDetalleSaldo) return

    const handleClickOutside = (event) => {
      if (saldoRef.current && !saldoRef.current.contains(event.target)) {
        setVerDetalleSaldo(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [verDetalleSaldo])

  return (
    <>
      <div className={styles.header} ref={headerRef}>
        {!isMobile && (
          <Link className={styles.ebm} to="/Publicaciones">
            <img className={styles.ebmLogo} src={logoEBM} alt="e-buymarket" />
          </Link>
        )}

        <div className={styles.areaMenu}>
          <button
            type="button"
            data-menu-toggle
            aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuAbierto}
            onClick={() => desplegarMenu(!menuAbierto)}
            className={styles.menuIcon}
          >
            <i className={`fa ${menuAbierto ? 'fa-times' : 'fa-bars'}`} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.contenedorSearch}>
          <CabeceraBuscador />
        </div>

        <div className={styles.areaCuenta}>
          {empid && idUsuario && (
            <div className={styles.cuenta}>
              {(saldoMensualDisponible !== null || limitePorCompra !== null) && (
                <div className={styles.saldoWrapper} ref={saldoRef}>
                  <button
                    type="button"
                    className={`${styles.saldoMensual} ${leyendaSaldo ? '' : styles.saldoSinDetalle}`}
                    title={leyendaSaldo || undefined}
                    aria-expanded={leyendaSaldo ? verDetalleSaldo : undefined}
                    onClick={
                      leyendaSaldo ? () => setVerDetalleSaldo((prevState) => !prevState) : undefined
                    }
                  >
                    {saldoMensualDisponible !== null ? (
                      <>
                        <span className={styles.saldoMonto}>
                          $
                          {formatearNumeroAR(
                            saldoMensualDisponible < 0 ? 0 : saldoMensualDisponible
                          )}
                        </span>

                        <div className={styles.saldoBarra}>
                          <div
                            className={`${styles.saldoBarraRelleno} ${
                              porcentajeDisponible <= UMBRAL_SALDO_CRITICO
                                ? styles.saldoBarraCritico
                                : porcentajeDisponible <= UMBRAL_SALDO_BAJO
                                  ? styles.saldoBarraBajo
                                  : ''
                            }`}
                            style={{ width: `${porcentajeDisponible}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <span className={styles.saldoLabelCompra}>Máx. por compra</span>
                    )}

                    {saldoMensualDisponible !== null && limitePorCompra !== null && (
                      <span className={styles.saldoLimiteCompra}>
                        Máx. por compra ${formatearNumeroAR(limitePorCompra)}
                      </span>
                    )}

                    {saldoMensualDisponible === null && limitePorCompra !== null && (
                      <span className={styles.saldoMonto}>
                        ${formatearNumeroAR(limitePorCompra)}
                      </span>
                    )}
                  </button>

                  {verDetalleSaldo && leyendaSaldo && (
                    <div className={styles.saldoLeyenda}>{leyendaSaldo}</div>
                  )}
                </div>
              )}

              <Dropdown align="end">
                <Dropdown.Toggle
                  as="button"
                  type="button"
                  className={`${styles.usuarioToggle}${enPerfil ? ` ${styles.perfil_active}` : ''}`}
                >
                  <ImagenPerfil idUsuario={idUsuario} clase={styles.perfil_img} />
                  <span className={styles.perfil}>{nombre}</span>
                  <i className={`fa fa-angle-down ${styles.usuarioChevron}`} aria-hidden="true" />
                </Dropdown.Toggle>

                <Dropdown.Menu className={styles.usuarioMenu}>
                  <Dropdown.Item as={Link} to="/Perfil" className={styles.usuarioMenuItem}>
                    <i className="fa fa-user" aria-hidden="true" />
                    Mi perfil
                  </Dropdown.Item>
                  <Dropdown.Divider className={styles.usuarioMenuDivider} />
                  <Dropdown.Item
                    as="button"
                    type="button"
                    onClick={handleRevokeToken}
                    className={`${styles.usuarioMenuItem} ${styles.usuarioMenuSalir}`}
                  >
                    <i className="fa fa-sign-out" aria-hidden="true" />
                    Salir
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>

        <div className={styles.acciones}>
          {idPerfil !== ID_PERFIL_VENDEDOR && (
            <div id="contenedorNotificacionCarrito">
              <Link
                to={'/Carrito'}
                id="notificacion-carrito"
                className="btn btn-notificaciones"
                aria-label={`Carrito (${cantidad})`}
                onClick={() => setVerCarrito(false)}
              >
                <i className="fa fa-shopping-cart" aria-hidden="true" />
                <span className="badge badge-light">{cantidad}</span>
              </Link>
            </div>
          )}

          <div id="esUnificado">
            <DropdownNotificacion refreshKey={refreshNotificaciones} />
          </div>
        </div>

        {!isMobile && (
          <nav className={styles.barra_acceso_directo} aria-label="Accesos directos">
            <MenuFavoritos />
          </nav>
        )}
      </div>

      <Modal
        show={mostrarAvisoSesion}
        onHide={cerrarSesionPorInactividad}
        backdrop={'static'}
        className="w-25 ml-auto mr-auto"
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Psst!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col">
              ¿Sigues ahí?..
              <br />
              Si no confirmas, tu sesión finalizará.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <LinkButton
            className="btn btn-danger"
            to="/login/"
            onClick={() => cerrarSesionPorInactividad()}
          >
            Cerrar Sesión
          </LinkButton>
          <LinkButton className="btn btn-primary" onClick={() => extenderSesion()}>
            ¡Aún sigo aquí!
          </LinkButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Cabecera
